/**
 * TTRPGPanel — smart wrapper for the live TTRPG side panel.
 *
 * Owns every external data dependency: useCompanyApi for the Eden Treaty
 * client, useAuth for the current user, useTTRPGEvents for the SSE stream
 * (DM/player coaching, participation %, current scene image, on-demand
 * coaching result), and the React Query graph (participants fetch +
 * characters fetch + joinSession + dm-coaching mutation). It accumulates
 * rolling history buffers (last 3 DM suggestions, last 3 player coaching
 * tips, last 4 scene images), computes a discriminated `mode` from the
 * resolved data, and forwards everything to TTRPGPanelView. The view at
 * ./TTRPGPanelView/index.tsx is the dumb half — no API hooks, no auth, no
 * SSE — which makes it Storybookable in isolation.
 */
import { useLocalStorage } from "@mantine/hooks"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import type { IDMCoachingRequest, IDMSuggestion, IPlayerCoaching } from "../../hooks/useSSE"
import { useTTRPGEvents } from "../../hooks/useSSE"
import { fetchCharacters, fetchParticipants, joinSession } from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import type { ICharacter, IParticipant, IProgressiveVisual, IProps } from "./interfaces"
import { TTRPGPanelView } from "./TTRPGPanelView"
import type { TViewProps } from "./TTRPGPanelView/interfaces"

export function TTRPGPanel({ meetingId, seriesId, isLive }: IProps): React.JSX.Element | null {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()

  const [collapsed, setCollapsed] = useLocalStorage({ key: `ttrpg-panel-collapsed-${meetingId}`, defaultValue: false })
  const [sceneCollapsed, setSceneCollapsed] = useLocalStorage({
    key: `ttrpg-scene-collapsed-${meetingId}`,
    defaultValue: false,
  })
  const [charPickerOpen, setCharPickerOpen] = useState(false)
  const [dmHistory, setDmHistory] = useState<IDMSuggestion[]>([])
  const [playerHistory, setPlayerHistory] = useState<IPlayerCoaching[]>([])
  const [visualHistory, setVisualHistory] = useState<IProgressiveVisual[]>([])
  const [localCoachingResult, setLocalCoachingResult] = useState<IDMCoachingRequest | null>(null)

  const { dmSuggestion, playerCoaching, currentVisual, participationData, dmCoachingRequest } =
    useTTRPGEvents(meetingId)

  // Accumulate rolling buffer of last 3 suggestions (not counting the current one shown)
  useEffect(() => {
    if (!dmSuggestion) return
    setDmHistory((prev) => {
      const next = [dmSuggestion, ...prev.filter((x) => x.timestamp !== dmSuggestion.timestamp)]
      return next.slice(1, 4)
    })
  }, [dmSuggestion])

  useEffect(() => {
    if (!playerCoaching) return
    setPlayerHistory((prev) => {
      const next = [playerCoaching, ...prev.filter((x) => x.timestamp !== playerCoaching.timestamp)]
      return next.slice(1, 4)
    })
  }, [playerCoaching])

  useEffect(() => {
    if (!currentVisual) return
    setVisualHistory((prev) => {
      const next = [currentVisual, ...prev.filter((x) => x.artifactId !== currentVisual.artifactId)]
      return next.slice(1, 5) // keep last 4 as history (current is shown separately)
    })
  }, [currentVisual])

  // When SSE delivers a coaching request, store it locally too
  useEffect(() => {
    if (!dmCoachingRequest) return
    setLocalCoachingResult(dmCoachingRequest)
  }, [dmCoachingRequest])

  const participantsQuery = useCompanyQuery<IParticipant[]>({
    key: (org) => queryKeys.ttrpgParticipants(org, meetingId),
    queryFn: (company) => fetchParticipants(company, meetingId),
    enabled: !!seriesId,
  })

  const charactersQuery = useCompanyQuery<ICharacter[]>({
    // seriesId is nullable here; the query is disabled until it resolves, so the
    // `?? ""` placeholder segment is never used for a live fetch.
    key: (org) => queryKeys.ttrpgCharacters(org, seriesId ?? ""),
    queryFn: (company) => {
      if (!seriesId) throw new Error("series unavailable")
      return fetchCharacters(company, seriesId)
    },
    enabled: !!seriesId,
  })

  const joinMutation = useCompanyMutation({
    mutationFn: (company, { role, characterId }: { role: "dm" | "player" | "observer"; characterId?: string }) =>
      joinSession(company, meetingId, role, characterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ttrpgParticipants(orgId, meetingId) })
    },
    onError: (err) => notifyError(err),
  })

  const coachMutation = useCompanyMutation<IDMCoachingRequest, void>({
    mutationFn: async (company): Promise<IDMCoachingRequest> => {
      // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
      const result = await (company.meetings as any)({ id: meetingId })["dm-coaching"].post() // eslint-disable-line @typescript-eslint/no-explicit-any
      if (result.error) throw new Error(extractErrorMessage(result.error))
      // Response is the standard `{ data }` envelope — peel it to the payload.
      return result.data.data as IDMCoachingRequest
    },
    onSuccess: (data) => {
      setLocalCoachingResult(data)
    },
    onError: (err) => notifyError(err),
  })

  const handleToggle = useCallback((): void => {
    setCollapsed((prev) => !prev)
  }, [setCollapsed])

  const handleSceneToggle = useCallback((): void => {
    setSceneCollapsed((prev) => !prev)
  }, [setSceneCollapsed])

  const handleJoinObserver = useCallback((): void => {
    joinMutation.mutate({ role: "observer" })
  }, [joinMutation])

  const handleJoinDM = useCallback((): void => {
    joinMutation.mutate({ role: "dm" })
  }, [joinMutation])

  const handleJoinPlayer = useCallback(
    (characterId: string): void => {
      joinMutation.mutate({ role: "player", characterId })
    },
    [joinMutation],
  )

  const handleAskCoach = useCallback((): void => {
    coachMutation.mutate()
  }, [coachMutation])

  // ---------------------------------------------------------------------
  // Mode resolution — pick exactly one of the five discriminants.
  // ---------------------------------------------------------------------

  // No series linked → render nothing.
  if (!seriesId) return <TTRPGPanelView mode="hidden" />

  // While loading participants show nothing (avoid flicker)
  if (participantsQuery.isLoading) return <TTRPGPanelView mode="hidden" />

  const participants = participantsQuery.data ?? []
  const myParticipant = participants.find((p) => p.userId === user?.id)
  const characters = charactersQuery.data ?? []

  // Not yet joined — show join banner (only when live)
  if (!myParticipant) {
    if (!isLive) return <TTRPGPanelView mode="hidden" />

    const bannerProps: TViewProps = {
      mode: "banner",
      charPickerOpen,
      onCharPickerChange: setCharPickerOpen,
      characters,
      charactersLoading: charactersQuery.isLoading,
      joinDMPending: joinMutation.isPending && joinMutation.variables?.role === "dm",
      joinPlayerPending: joinMutation.isPending && joinMutation.variables?.role === "player",
      onJoinDM: handleJoinDM,
      onJoinPlayer: handleJoinPlayer,
      onJoinObserver: handleJoinObserver,
    }
    return <TTRPGPanelView {...bannerProps} />
  }

  // Joined as DM
  if (myParticipant.role === "dm") {
    const dmProps: TViewProps = {
      mode: "dm",
      currentVisual,
      visualHistory,
      sceneCollapsed,
      onSceneToggle: handleSceneToggle,
      dmSuggestion,
      dmHistory,
      participationData,
      collapsed,
      onToggle: handleToggle,
      coachingResult: localCoachingResult,
      coachingPending: coachMutation.isPending,
      onAskCoach: handleAskCoach,
    }
    return <TTRPGPanelView {...dmProps} />
  }

  // Joined as player
  if (myParticipant.role === "player") {
    const character = characters.find((c) => c.id === myParticipant.characterId) ?? null
    const playerProps: TViewProps = {
      mode: "player",
      currentVisual,
      visualHistory,
      sceneCollapsed,
      onSceneToggle: handleSceneToggle,
      playerCoaching,
      playerHistory,
      characterName: character?.name ?? null,
      collapsed,
      onToggle: handleToggle,
    }
    return <TTRPGPanelView {...playerProps} />
  }

  // Observer — scene panel only (if any)
  const observerProps: TViewProps = {
    mode: "observer",
    currentVisual,
    visualHistory,
    sceneCollapsed,
    onSceneToggle: handleSceneToggle,
  }
  return <TTRPGPanelView {...observerProps} />
}
